<script>
import design from '@design'
import FavoriteHeart from '@components/favorite-heart.vue'
import LoaderIcon from '@components/loader-icon.vue'
import { addFavoriteApp, deleteFavoriteApp } from '@src/api/apps'
import { appsComputed } from '@state/helpers'
import { isMobileMixin } from '@src/mixins/isMobileMixin'

export default {
  components: {
    FavoriteHeart,
    LoaderIcon,
  },
  mixins: [isMobileMixin],
  props: {
    name: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    cardImage: {
      type: String,
      default: '',
    },
    cardImageMobile: {
      type: String,
      default: '',
    },
    category: {
      type: String,
      default: '',
    },
    appUrl: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    options: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      defaultOptions: {
        mobileCardSize: {
          width: 98,
          height: 98,
        },
        showHeart: true,
        showDescription: false,
        forceMobileDesign: false,
      },
      defaultCardImage: require('@assets/images/app_card_example.png'),
      isTransparent: false,
      errorLoadingImageDesktop: false,
      errorLoadingImageMobile: false,
      loadingFavorite: false,
    }
  },
  computed: {
    ...appsComputed,
    cardImagePath() {
      if (this.errorLoadingImageDesktop) {
        return this.defaultCardImage
      }
      return this.cardImage || this.defaultCardImage
    },

    normalizedOptions() {
      return { ...this.defaultOptions, ...this.options }
    },

    cardImagePathMobile() {
      if (this.errorLoadingImageMobile && this.errorLoadingImageDesktop) {
        return this.defaultCardImage
      }

      if (this.errorLoadingImageMobile) {
        return this.cardImage
      }
      return this.cardImageMobile || this.cardImage || this.defaultCardImage
    },
    appBackgroundColor() {
      const defaultColor = design['secondary-light-blue']
      const category = this.category ? this.category.toLowerCase() : ''
      const categoryColors = {
        research: design['secondary-orange'],
        tools: design['secondary-green'],
        marketplace: design['secondary-red'],
        bookmarks: design['secondary-light-blue'],
      }

      return categoryColors[category] || defaultColor
    },
    isFavorite() {
      return this.isAppFavorite(this.appId)
    },
    mobileCardImageDimensions() {
      return {
        width: this.normalizedOptions?.mobileCardSize?.width,
        height: this.normalizedOptions?.mobileCardSize?.height,
      }
    },
    isMobileDesign() {
      return this.isMobile || this.normalizedOptions?.forceMobileDesign
    },
  },
  created() {
    this.testImageLoad(this.cardImage)
    this.testImageLoadMobile(this.cardImageMobile)
  },
  methods: {
    async favoriteApp() {
      await addFavoriteApp({ appId: this.appId })
      this.$store.commit('apps/FAVORITE_APP_BY_ID', this.appId)
    },

    async unfavoriteApp() {
      await deleteFavoriteApp({ appId: this.appId })
      this.$store.commit('apps/UNFAVORITE_APP_BY_ID', this.appId)
    },

    async handleHeartClick() {
      if (this.loadingFavorite) {
        return
      }
      this.loadingFavorite = true
      if (this.isFavorite) {
        await this.unfavoriteApp()
      } else {
        await this.favoriteApp()
      }
      this.loadingFavorite = false
    },

    testImageLoad(src) {
      if (!src) {
        this.errorLoadingImageDesktop = true
        return
      }
      const testImage = new Image()
      testImage.onerror = () => {
        this.errorLoadingImageDesktop = true
      }
      testImage.src = src
    },

    testImageLoadMobile(src) {
      if (!src) {
        this.errorLoadingImageMobile = true
        return
      }
      const testImage = new Image()
      testImage.onerror = () => {
        this.errorLoadingImageMobile = true
      }
      testImage.src = src
    },

    redirectToAppUrl(url) {
      window.open(url, '_blank')
    },
  },
}
</script>

<template>
  <div>
    <div
      v-if="!isMobileDesign"
      :class="{ [$style.appBlock]: true, 'light-transparent': isTransparent }"
      @mouseover="isTransparent = true"
      @mouseout="isTransparent = false"
      @click="redirectToAppUrl(appUrl)">
      <div
        :class="$style.cardImageContainer"
        :style="{
          'background-image': 'url(\'' + encodeURI(cardImagePath) + '\')',
        }" />
      <div :class="$style.appDescriptionBlock">
        <div
          :class="{
            [$style.appCategoryTag]: true,
            [$style.appCategoryTagInTheMiddle]: normalizedOptions.showDescription,
          }"
          class="tag-text"
          :style="{ 'background-color': appBackgroundColor }">
          {{ category }}
        </div>
        <div v-if="normalizedOptions.showDescription" :class="$style.appDescription">
          <p class="button-text color-primary-blue mt-15">{{ name }}</p>
          <p class="p2 color-primary-charcoal">{{ description }}</p>
        </div>
      </div>
      <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
      <FavoriteHeart
        v-else-if="appId"
        :class="$style.heartIcon"
        :is-favorite="isFavorite"
        @handleHeartClick="handleHeartClick"
        @mouseover.stop="isTransparent = false" />
    </div>
    <div
      v-if="isMobileDesign && !normalizedOptions.showDescription"
      class="d-flex"
      :class="{
        [$style.appBlockMobile]: true,
        'light-transparent': isTransparent,
      }"
      :style="{
        width: `${mobileCardImageDimensions.width}px`,
      }"
      @mouseover="isTransparent = true"
      @mouseout="isTransparent = false"
      @click="redirectToAppUrl(appUrl)">
      <div
        :class="$style.cardImageContainerMobile"
        :style="{
          'background-image': 'url(\'' + encodeURI(cardImagePathMobile) + '\')',
          width: `${mobileCardImageDimensions.width}px`,
          height: `${mobileCardImageDimensions.height}px`,
        }" />
      <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
      <FavoriteHeart
        v-else-if="normalizedOptions.showHeart"
        :class="$style.heartIcon"
        :is-favorite="isFavorite"
        @handleHeartClick="handleHeartClick"
        @mouseover.stop="isTransparent = false" />
    </div>
    <div
      v-if="isMobileDesign && normalizedOptions.showDescription"
      class="d-flex"
      :class="{
        [$style.appBlockMobileWithDescription]: true,
        'light-transparent': isTransparent,
      }">
      <div class="position-relative">
        <div
          :class="$style.cardImageContainerMobileWithDescription"
          :style="{
            'background-image': 'url(\'' + encodeURI(cardImagePathMobile) + '\')',
          }"
          @click="redirectToAppUrl(appUrl)" />
        <LoaderIcon v-if="loadingFavorite" :class="$style.heartIconMobileWithDescription" class="d-inline" />
        <FavoriteHeart
          v-else-if="appId"
          :class="$style.heartIconMobileWithDescription"
          :is-favorite="isFavorite"
          @handleHeartClick="handleHeartClick"
          @mouseover.stop="isTransparent = false" />
      </div>

      <div class="d-flex flex-column">
        <h4 class="color-primary-charcoal mb-10 font-weight-bold cursor-pointer" @click="redirectToAppUrl(appUrl)">
          {{ name }}
        </h4>

        <div
          :class="$style.appCategoryTagMobileWithDescription"
          class="tag-text"
          :style="{ 'background-color': appBackgroundColor }">
          {{ category }}
        </div>
        <p class="p1" @click="redirectToAppUrl(appUrl)">{{ description }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.appBlock {
  position: relative;
  display: flex;
  flex-direction: column;
  width: fit-content;
  height: 100%;
  cursor: pointer;
  border: 0.5px solid $lightest-gray;
  border-radius: 8px 0 0 8px;
  box-shadow: 3px 3px 6px rgba(196, 196, 196, 0.3);
  transition: opacity 0.3s ease;
}

.appBlockMobile {
  position: relative;
  display: flex;
  flex-direction: column;
  width: fit-content;
  height: 100%;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.cardImageContainer {
  flex: 0 0 107px;
  width: 193px;
  height: 107px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  border-bottom: 0.5px solid $lightest-gray;
  border-radius: 8px 8px 0 8px;
}

.cardImageContainerMobile {
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: 0.5px solid $lightest-gray;
  border-radius: 16px;
  box-shadow: 3px 3px 6px rgba(196, 196, 196, 0.3);
}

.appNameMobile {
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
  font-size: 10px;
  color: $dark-gray;
  text-align: center;
}

.appDescriptionBlock {
  position: relative;
  flex-grow: 1;
  width: 193px;
  height: auto;
  background: $white;
  border-radius: 0 0 0 8px;

  p {
    padding: 8px 16px;
  }
}

.appDescription {
  p:first-child {
    padding: 0 16px;
  }

  p:last-child {
    padding: 4px 16px 19px;
  }
}

.heartIcon {
  @media (max-width: $lg-breakpoint) {
    top: 8px;
    right: 9px;
  }

  position: absolute;
  top: 13px;
  right: 17px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.appCategoryTag {
  position: absolute;
  top: -24px;
  right: -1px;
  display: flex;
  align-items: center;
  height: 18px;
  padding: 2px 16px;
  color: $white;
  border: 1px solid $white;
  border-radius: 50px 0 0 50px;
}

.appCategoryTagInTheMiddle {
  top: -9px;
}

.cardImageContainerMobileWithDescription {
  flex-shrink: 0;
  width: 98px;
  height: 98px;
  margin-right: 24px;
  cursor: pointer;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: 0.5px solid $lightest-gray;
  border-radius: 16px;
  box-shadow: 3px 3px 6px rgba(196, 196, 196, 0.3);
}

.appCategoryTagMobileWithDescription {
  display: flex;
  align-items: center;
  width: fit-content;
  height: 18px;
  padding: 2px 16px;
  margin-bottom: 8px;
  color: $white;
  border-radius: 48px;
}

.heartIconMobileWithDescription {
  position: absolute;
  top: 8px;
  right: 33px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}
</style>
